// src/components/widgets/WidgetPreviewShadcn.jsx
import React, { useEffect, useRef, useMemo, useState } from 'react';
import {
  Eye,
  Monitor,
  Smartphone,
  Tablet,
  ExternalLink,
  Maximize2,
  RefreshCw,
  AlertCircle
} from 'lucide-react';

// shadcn components
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';

import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../utils/cn';

const WidgetPreviewShadcn = ({
  widget,           // Widget data (either selectedWidget or formData)
  refreshTrigger,   // Only update when this changes
  onMaximize,
  isMaximized = false,
  isIntegrated = false,
  className = ''
}) => {
  const { currentUser } = useAuth();
  const [previewMode, setPreviewMode] = React.useState('desktop');
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);

  // IFrame ref for control
  const iframeRef = useRef(null);

  // Preview dimensions for different devices - maximize vertical space
  const previewDimensions = {
    desktop: {
      width: '100%',
      height: '100%',
      maxWidth: '100%',
      maxHeight: '100%'
    },
    tablet: {
      width: '768px',
      height: '100%',
      maxWidth: '768px',
      maxHeight: '100%'
    },
    mobile: {
      width: '375px',
      height: '100%',
      maxWidth: '375px',
      maxHeight: '100%'
    }
  };

  const currentDimensions = previewDimensions[previewMode];

  // Generate preview URL only when refreshTrigger changes
  const previewUrl = useMemo(() => {
    if (!widget || !currentUser || !widget.widget_name) return null;

    // Get business slug from authenticated user context
    const businessSlug = currentUser.business?.slug || 'demo-business';

    // Build URL with widget configuration
    const backendUrl = process.env.NODE_ENV === 'development'
      ? 'http://localhost:5001'
      : window.location.origin;

    const widgetPath = `/widget/${widget.widget_type}/${businessSlug}/${encodeURIComponent(widget.widget_name)}`;

    // Include current widget configuration in URL params
    const params = new URLSearchParams({
      preview: 'true',
      timestamp: Date.now(), // Prevent caching
      config: JSON.stringify({
        primary_color: widget.primary_color,
        theme: widget.theme,
        border_radius: widget.border_radius,
        configuration: widget.configuration
      })
    });

    return `${backendUrl}${widgetPath}?${params}`;
  }, [
    widget?.widget_name,      // Only regenerate URL when name changes
    widget?.widget_type,      // Or widget type changes
    widget?.primary_color,    // Or appearance changes
    widget?.theme,
    widget?.border_radius,
    widget?.configuration,
    currentUser?.business?.slug,
    refreshTrigger            // Or when explicitly refreshed
  ]);

  // Handle iframe load events
  const handleIframeLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  // Start loading when URL changes
  useEffect(() => {
    if (previewUrl) {
      setIsLoading(true);
      setHasError(false);
    }
  }, [previewUrl]);

  // Handle manual refresh
  const handleRefresh = () => {
    if (iframeRef.current) {
      setIsLoading(true);
      setHasError(false);
      // Force reload by changing src
      iframeRef.current.src = iframeRef.current.src;
    }
  };

  // Handle "Open in new tab" functionality
  const handleOpenInNewTab = () => {
    if (previewUrl) {
      window.open(previewUrl, '_blank');
    }
  };

  if (!widget) {
    return (
      <Card className={cn("h-full", className)}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Preview
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Eye className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">Select a widget to see preview</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={cn("h-full flex flex-col", className)}>
      {/* Minimal Control Bar */}
      <div className="flex-shrink-0 flex items-center justify-center gap-2 py-2 px-4 border-b bg-card">
        {/* Device Selector */}
        <div className="flex rounded-lg border p-1">
          <Button
            variant={previewMode === 'desktop' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setPreviewMode('desktop')}
            className="h-7 w-7 p-0"
            title="Desktop view"
          >
            <Monitor className="w-4 h-4" />
          </Button>
          <Button
            variant={previewMode === 'tablet' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setPreviewMode('tablet')}
            className="h-7 w-7 p-0"
            title="Tablet view"
          >
            <Tablet className="w-4 h-4" />
          </Button>
          <Button
            variant={previewMode === 'mobile' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setPreviewMode('mobile')}
            className="h-7 w-7 p-0"
            title="Mobile view"
          >
            <Smartphone className="w-4 h-4" />
          </Button>
        </div>

        {/* Refresh Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isLoading}
          className="h-7 w-7 p-0"
          title="Refresh preview"
        >
          <RefreshCw className={cn("w-4 h-4", isLoading && "animate-spin")} />
        </Button>

        {/* External Link Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleOpenInNewTab}
          className="h-7 w-7 p-0"
          title="Open in new tab"
        >
          <ExternalLink className="w-4 h-4" />
        </Button>
      </div>

      {/* Preview Area with IFrame - Maximum space */}
      <div className="flex-1 bg-muted/30 overflow-hidden p-4">
        <div className="h-full flex items-center justify-center">
          <div
            className="bg-background shadow-xl border transition-all duration-300 overflow-hidden relative"
            style={{
              width: currentDimensions.width,
              height: currentDimensions.height,
              maxWidth: currentDimensions.maxWidth,
              maxHeight: currentDimensions.maxHeight,
              borderRadius: `${widget.border_radius || 8}px`
            }}
          >
            {/* Loading Overlay */}
            {isLoading && (
              <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-10">
                <div className="text-center">
                  <RefreshCw className="w-8 h-8 text-primary animate-spin mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Loading preview...</p>
                </div>
              </div>
            )}

            {/* Error State */}
            {hasError && (
              <div className="absolute inset-0 bg-background flex items-center justify-center z-10">
                <div className="text-center p-6">
                  <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-3" />
                  <h3 className="text-lg font-medium mb-2">Preview Error</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Failed to load widget preview. The widget backend might not be ready yet.
                  </p>
                  <Button onClick={handleRefresh}>
                    Try Again
                  </Button>
                </div>
              </div>
            )}

            {/* Actual IFrame */}
            {previewUrl && (
              <iframe
                ref={iframeRef}
                src={previewUrl}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                className="w-full h-full border-0"
                title={`${widget.widget_name || 'Widget'} Preview`}
                sandbox="allow-scripts allow-same-origin allow-forms"
                style={{
                  borderRadius: `${widget.border_radius || 8}px`
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WidgetPreviewShadcn;